// Credit: https://github.com/javisperez/tailwindcolorshades/blob/master/src/composables/colors.ts
// Ported from legacy ui-kit color-utils for the new-tree host-primary bridge (#8066).
// Intentionally independent of shared/ui-kit.

import convert from 'color-convert';

const CMY_HUES = [180, 300, 60];
const RGB_HUES = [360, 240, 120, 0];

/** Tailwind-style ramp intensities for steps 100–400 and 600–900. */
export const INTENSITY_MAP: Readonly<Record<number, number>> = {
  100: 0.9,
  200: 0.75,
  300: 0.6,
  400: 0.3,
  600: 0.9,
  700: 0.75,
  800: 0.6,
  900: 0.45,
};

export const PRIMARY_RAMP_STEPS = [100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

export type PrimaryRampStep = (typeof PRIMARY_RAMP_STEPS)[number];

const hueShift = (hues: number[], hue: number, intensity: number) => {
  const closestHue = [...hues].sort((a, b) => Math.abs(a - hue) - Math.abs(b - hue))[0]!;
  const shift = closestHue - hue;
  return Math.round(intensity * shift * 0.5);
};

export const lighten = (hex: string, intensity: number): string => {
  if (!hex) return '';

  const [h, s, v] = convert.hex.hsv(hex);
  const nextHue = h + hueShift(CMY_HUES, h, intensity);
  const saturation = s - Math.round(s * intensity);
  const value = v + Math.round((100 - v) * intensity);

  return `#${convert.hsv.hex([nextHue, saturation, value])}`;
};

export const darken = (hex: string, intensity: number): string => {
  if (!hex) return '';

  const inverseIntensity = 1 - intensity;
  const [h, s, v] = convert.hex.hsv(hex);
  const nextHue = h + hueShift(RGB_HUES, h, inverseIntensity);
  const saturation = s + Math.round((100 - s) * inverseIntensity);
  const value = v - Math.round(v * inverseIntensity);

  return `#${convert.hsv.hex([nextHue, saturation, value])}`;
};

export const sixDigitsColorHex = (hexColor: string): string => {
  const hexValue = hexColor.replace('#', '');
  return `#${hexValue.length === 3 ? hexValue.replace(/(.)/g, '$1$1') : hexValue.padEnd(6, '0')}`;
};

/** Returns `#000000` or `#ffffff` for WCAG on-accent pairing (ADR #8066). */
export const getOnAccentTextHex = (hexColor: string): string => {
  const [r, g, b] = convert.hex.rgb(sixDigitsColorHex(hexColor));

  // Credit: https://gomakethings.com/dynamically-changing-the-text-color-based-on-background-color-contrast-with-vanilla-js/
  return (r * 299 + g * 587 + b * 114) / 1000 >= 128 ? '#000000' : '#ffffff';
};

export const derivePrimaryRampStep = (anchorHex: string, step: PrimaryRampStep): string => {
  const normalizedAnchor = sixDigitsColorHex(anchorHex);
  if (step === 500) {
    return normalizedAnchor;
  }

  const intensity = INTENSITY_MAP[step];
  return step > 500 ? darken(normalizedAnchor, intensity) : lighten(normalizedAnchor, intensity);
};

/** Custom-property reads return hex strings, so a normalize + lowercase is enough to compare. */
export const normalizeHexForCompare = (color: string): string => sixDigitsColorHex(color.trim()).toLowerCase();
